<template>
	<teleport to="#pdf-vuer-overlay-teleport">
		<div class="pdf-viewer-wrapper">
            <!-- Header -->
            <div
            class="header"
            :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
                <div class="relative-content-helper">
                    <button-tertiary
                    class="back-icon"
                    buttonStyle="round"
                    icon="arrow-left-full"
                    @clicked="emit('close')"/>
                    <!-- Title -->
                    <div class="title-wrapper">
                        <div class="title">{{title}}</div>
                    </div>
                    <!-- Download Button -->
                    <button-tertiary
                    v-if="hasDowloadButton"
                    buttonStyle="round"
                    icon="download"
                    class="download-button"
                    @clicked="downloadLink = fileUrl"/>
                </div>
            </div>
			<!-- Pdf Files -->
			<div
			ref="scrollElementRef"
			class="pdf-wrapper"
			:class="{'mobile-wrapper': getMobileAppState}"
			@scroll="valueScrolled = $event.target.scrollTop">
				<!-- Loader Generic -->
				<loaders-generic
				v-if="showLoader || isOverlayLoader"
				:fullSize="showLoader"
				:fullSizeOverlay="isOverlayLoader"
				:isOverlay="isOverlayLoader"/>
				<!-- Pdf display -->
				<vue-pdf-embed
				v-if="blob"
				:source="blob"
				@loaded="showLoader = false"
				@loading-failed="errorHandler"/>
			</div>
			<!-- Viewer Footer -->
			<navigation-footer
			:buttonsParams="buttonsFooterData"
			:showFooter="scrollIsAtEnd && !showLoader"
			:buttonFullWidth="buttonFullWidth"/>
			<!-- Component to handle download logic-->
			<download-logic-component
			:downloadLink="downloadLink"
			@clearDownloadLink="downloadLink = null"/>
		</div>
	</teleport>
	<!-- Toast -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import VuePdfEmbed from 'vue-pdf-embed'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import NavigationFooter from '@/components/generic/navigation/Footer'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Asset */
import { emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, onMounted, computed } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { mobileClientDownloadValidator } from '@/network/clients/mobileClientSetup'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getMobileAppState, getDeviceInUse } = mapGetters()

/* Emmitters */
const emit = defineEmits(['close', 'showErrorToast'])

/** Props received:
@fileUrl { String }
@title { String }
@hasDowloadButton {Boolean} - prop to display button for download
@buttonsFooterData {Array} - array of buttons to display on detail footer
@buttonFullWidth {Boolean}
@linkIsLocalFile {Boolean} prop to tells us if we need to concat with link our env base url
*/
const props = defineProps({
	fileUrl: String,
	title: String,
    hasDowloadButton: Boolean,
	buttonsFooterData: Array,
	buttonFullWidth: Boolean,
    linkIsLocalFile: Boolean
})

const showLoader = ref(true)
const isOverlayLoader = ref(false)
const blob = ref(null)
const fileUrl = ref(null)
const scrollElementRef = ref('')
const valueScrolled = ref(0)
const downloadLink = ref(null)

const scrollIsAtEnd = computed(() => {
	if (valueScrolled.value === 0) return false
    return Math.abs(scrollElementRef.value.scrollHeight - scrollElementRef.value.scrollTop - scrollElementRef.value.clientHeight) < 1
})

const initialFileDownloader = async () => {
	showLoader.value = true
    let fileUrl = props.fileUrl

    if (props.linkIsLocalFile) fileUrl = fileUrl.replace('./files', process.env.VUE_APP_WEB_BASE_URL + '/files')

	try {
        const res = await mobileClientDownloadValidator({ method: 'GET', url: fileUrl })

        let fileName
        if (res.headers['content-disposition']) {
            const splitVariable = res.headers['content-disposition'].includes('fileName') ? 'fileName=' : 'filename='
            fileName = res.headers['content-disposition'].split(splitVariable)[1]
        } else fileName = 'download.pdf'

        const filePath = window.cordova.file.dataDirectory + fileName

        await mobileClientDownloadValidator({ method: 'downloadFile', url: fileUrl, filePath: filePath })

        window.resolveLocalFileSystemURL(filePath, (fileEntry) => {
            fileEntry.file(file => {
                var reader = new FileReader()
                reader.onload = e => {
                    var base64Img = e.target.result
                    blob.value = base64Img
                }

                reader.readAsDataURL(file)
            })
        })
	} catch (error) {
		errorHandler(error.response)
	}
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
	isOverlayLoader.value = false
	feedbackToastData.value = feedbackToastData.value = {
        toastType: 'negative',
        text: 'erro',
        buttons: [{
            text: t('commonButtons.gotIt'),
            callbackAction: () => { showFeedbackToast.value = false }
        }]
    }
	showFeedbackToast.value = true
}

onMounted(() => {
	if (getMobileAppState.value) initialFileDownloader()
    else blob.value = props.fileUrl
})
</script>

<style lang="scss" scoped>
.pdf-viewer-wrapper {
	@include viewerOverlayTeleportStyles;

    .header {
        width: 100%;
        height: 76px;
        padding-top: 16px;

        display: flex;
        justify-content: flex-start;

        background-image: var(--gradient-horizontal);

        &.mobile-status-bar-android {
            height: calc(76px + var(--android-top-padding));
            padding-top: calc(16px + var(--android-top-padding));
        }

        &.mobile-status-bar-ios {
            height: calc(76px + constant(safe-area-inset-top));
            height: calc(76px + env(safe-area-inset-top));

            padding-top: calc(16px + constant(safe-area-inset-top));
            padding-top: calc(16px + env(safe-area-inset-top));
        }

        .relative-content-helper {
            width: 100%;
            height: 40px;
            position: relative;

            display: flex;
            justify-content: flex-start;

            .back-icon {
                position: absolute;
                top: 0;
                left: 16px;
            }

            .title-wrapper {
                width: 100%;
                height: 40px;
                padding: 0 40px;

                display: flex;
                flex-direction: column;
                justify-content: center;
                text-align: center;

                .title {
                    width: 100%;
                    height: 20px;
                    min-height: 20px;

                    @include H5-Medium;
                    line-height: 20px;
                    color: #FFFFFF;
                }
            }

            .download-button {
                position: absolute;
                top: 0;
                right: 16px;
            }
        }
    }

	.pdf-wrapper {
		position: relative;
		width: 100%;
		height: calc(100% - 60px);
		padding: 20px;

		display: flex;
		flex-direction: column;
		align-items: center;

		overflow: auto;
        overflow-x: hidden;

		&:not(.mobile-wrapper) {
			@include generic-scrollbar(var(--secondary-light-4), var(--primary));
        }

		.vue-pdf-embed {
			width: 100%;
			max-width: 900px;

			:deep(div) {
				margin-bottom: 24px;
				box-shadow: 0 2px 8px 4px rgba(0, 0, 0, 0.1);
			}
		}
	}
}
</style>
